import { useThemeContext } from "@src/context/ThemeContext";
import { useLabelMode } from "@src/hooks/useLabelMode";
import type {
  ThemePageContent,
  ThemePageContentValue,
  ThemePageName,
} from "@src/interfaces/theme-config.interface";
import { useCallback } from "react";

/**
 * Resolves a themed content value to a string. `{ patient, self }` pairs are
 * resolved by the current label voice; plain strings pass through unchanged.
 */
const resolveThemeValue = (value: ThemePageContentValue, mode: "patient" | "self"): string =>
  typeof value === "string" ? value : value[mode];

/**
 * Returns themed page copy when present and otherwise falls back to the caller's default text.
 */
export const useThemePageContent = () => {
  const { activeThemeConfig } = useThemeContext();

  return useCallback(
    <T extends ThemePageContent>(pageName: ThemePageName, defaultContent: T): T => {
      const themedContent = activeThemeConfig.content?.pages?.[pageName];
      return themedContent ? { ...defaultContent, ...themedContent } : defaultContent;
    },
    [activeThemeConfig],
  );
};

/**
 * Compatibility helper for single-key lookups. Resolves `{ patient, self }`
 * themed values to the correct voice using role/age precedence.
 */
export const useThemePageText = () => {
  const { activeThemeConfig } = useThemeContext();
  const mode = useLabelMode();

  return useCallback(
    (pageName: ThemePageName, textKey: string, defaultText: string): string => {
      const themedValue = activeThemeConfig.content?.pages?.[pageName]?.[textKey];
      return themedValue !== undefined ? resolveThemeValue(themedValue, mode) : defaultText;
    },
    [activeThemeConfig, mode],
  );
};
